import { useEffect, useState } from "react";
import { getSsrCatalogue } from "../services/api";

/**
 * Meal codes and their names, from the server rather than a copy.
 *
 * There were two hardcoded lists of these — one on the passenger form and one
 * on the ticket — and the ticket's had already drifted four codes behind, so a
 * passenger who ordered a Vegetarian Oriental saw "VOML" printed on it. The
 * catalogue is the one source; this is how a component reads it.
 *
 * Fetched once per session and shared. The list is static, several components
 * want it, and a request each would be pure waste.
 */

let cached: Promise<{ codes: Set<string>; labels: Map<string, string> }> | null = null;

function load() {
    if (!cached) {
        cached = getSsrCatalogue()
            .then((c) => ({
                codes: new Set((c.meal ?? []).map((m) => m.code)),
                labels: new Map((c.meal ?? []).map((m) => [m.code, m.label])),
            }))
            // A failed fetch must not be remembered as an empty catalogue for
            // the rest of the session — clear it so the next mount retries.
            .catch(() => {
                cached = null;
                return { codes: new Set<string>(), labels: new Map<string, string>() };
            });
    }
    return cached;
}

export function useMealCatalogue() {
    const [state, setState] = useState<{ codes: Set<string>; labels: Map<string, string> }>(
        { codes: new Set(), labels: new Map() },
    );

    useEffect(() => {
        let live = true;
        load().then((v) => { if (live) setState(v); });
        return () => { live = false; };
    }, []);

    // Until it arrives the ticket falls back to printing the raw code, which is
    // what it did for four codes permanently before this.
    return { mealCodes: state.codes, mealLabel: state.labels };
}
